import './Styles/AdminPanelStyles.css'

import { useEffect, useState } from 'react'
import { Nav, Table } from 'react-bootstrap'
import { toast } from 'react-toastify'
import ErrorBoundary from '../../Components/ErrorBoundary/ErrorBoundary'
import ScrollUpArrow from '../../Components/Components/ScrollUpArrow'
import LoadingSpinner from '../../Components/Components/LoadingSpinner'
import { useConfig } from '../../Providers/ConfigProvider'
import { GetAllParameters, GetAllSiteParameters } from '../../Services/ParameterService'
import { PARAM_GROUPS } from './Config/adminParamsConfig'
import { SITE_PARAM_GROUPS } from './Config/adminSiteParamsConfig'
import ParamRow from './Components/ParamRow'
import SiteParamRow from './Components/SiteParamRow'
import { useAuth } from '../../Providers/AuthProvider'
import NoAccess from '../../Components/NoAccess'
import MobileAccountNavBar from '../../Components/MobileComponents/NavBarMobile.jsx/MobileAccountNavBar'
import { AccountCurrentView } from '../../Data/PageModeEnums.ts'
import { useParams } from '../../Providers/ParamsProvider'
import AdminPanelNavBar from './Components/AdminPanelNavBar'
import AdminPanelSlider from './Components/AdminPanelSlider'
import AdminPanelFooter from './Components/AdminPanelFooter'

const AdminPanelPageIndex = () => {
    const { isMobile } = useParams()
    const { authState } = useAuth()
    const { api } = useConfig()
    const [activeTab, setActiveTab] = useState("system")
    const [paramsMap, setParamsMap] = useState(new Map())
    const [siteIds, setSiteIds] = useState([])
    const [siteParamsMap, setSiteParamsMap] = useState(new Map())
    const [loading, setLoading] = useState(true)
    const [hasError, setHasError] = useState(false)

    useEffect(() => {
        if (authState.user?.isOperator && authState.user?.upR_ADMIN) {
            setLoading(true)
            Promise.all([GetAllParameters(api), GetAllSiteParameters(api)])
                .then(([paramsRes, siteRes]) => {
                    setParamsMap(new Map(paramsRes.data.map(p => [p.paramId, p])))

                    const outerMap = new Map()
                    siteRes.data.forEach(row => {
                        if (!outerMap.has(row.siteId)) {
                            outerMap.set(row.siteId, new Map())
                        }
                        outerMap.get(row.siteId).set(row.paramId, row)
                    })
                    setSiteIds([...outerMap.keys()].sort())
                    setSiteParamsMap(outerMap)
                })
                .catch(() => {
                    setHasError(true)
                    toast.error('Błąd podczas ładowania parametrów.')
                })
                .finally(() => setLoading(false))
        }
    }, [api])

    const activeMap = siteParamsMap.get(activeTab) ?? new Map()

    const handleParamSaved = (paramId, valueKey, newValue) => {
        setParamsMap(prev => {
            const next = new Map(prev)
            next.set(paramId, { ...next.get(paramId), [valueKey]: newValue })
            return next
        })
    }

    const handleSiteParamSaved = (siteId, paramId, valueKey, newValue) => {
        setSiteParamsMap(prev => {
            const outerNext = new Map(prev)
            const innerNext = new Map(outerNext.get(siteId))
            innerNext.set(paramId, { ...innerNext.get(paramId), [valueKey]: newValue })
            outerNext.set(siteId, innerNext)
            return outerNext
        })
    }

    const getTabKind = () => {
        switch (activeTab) {
            case 'system':
                return 'system'
            case 'navBar':
                return 'navBar'
            case 'slider':
                return 'slider'
            case 'footer':
                return 'footer'
            default:
                return siteIds.includes(activeTab) ? 'site' : 'unknown'
        }
    }

    const generateTabContent = () => {
        switch (getTabKind()) {
            case 'system':
                if (PARAM_GROUPS && PARAM_GROUPS.length > 0) {
                    return PARAM_GROUPS.map(group => (
                        <div key={group.label} className='mb-4'>
                            <h5 className='fw-semibold border-bottom pb-2 mb-0'>{group.label}</h5>
                            <Table hover className='mb-0' responsive>
                                <tbody>
                                    {group.params.map(param => (
                                        <ParamRow
                                            key={param.id}
                                            param={param}
                                            dbRow={paramsMap.get(param.id)}
                                            api={api}
                                            onSaved={handleParamSaved}
                                        />
                                    ))}
                                </tbody>
                            </Table>
                        </div>))
                }
                break
            case 'navBar':
                return <AdminPanelNavBar />
            case 'slider':
                return <AdminPanelSlider />
            case 'footer':
                return <AdminPanelFooter />
            case 'site':
                if (SITE_PARAM_GROUPS && SITE_PARAM_GROUPS.length > 0) {
                    return SITE_PARAM_GROUPS.map(group => (
                        <div key={group.label} className='mb-4'>
                            <h5 className='fw-semibold border-bottom pb-2 mb-0'>{group.label}</h5>
                            <Table hover className='mb-0' responsive>
                                <tbody>
                                    {group.params.map(param => (
                                        <SiteParamRow
                                            key={`${activeTab}-${param.id}`}
                                            param={param}
                                            dbRow={activeMap.get(param.id)}
                                            siteId={activeTab}
                                            api={api}
                                            onSaved={handleSiteParamSaved}
                                        />
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ))
                }
                break
            default:
                return null
        }
    }

    if (!authState.user?.isOperator || !authState.user?.upR_ADMIN) {
        return <NoAccess
            isLicenseError={false}
            customErrorText={'Brak uprawnień do przeglądania panelu administracyjnego!'}
        />
    }
    return (
        <ErrorBoundary>
            <ScrollUpArrow />
            <div className='container-fluid-with-paddings mt-4 admin-panel' >
                {isMobile && <div className='mb-2'><MobileAccountNavBar currentKey={AccountCurrentView.PanelAdministratora} /></div>}
                <div className='main-container-holder'>
                    <span className='d-flex align-items-center justify-content-center primary-accent-color-text'>Po zmianie parametrów pamiętaj aby przeładowac aplikacje!</span>
                    <h2 className='mt-2 mb-4'>Panel administratora</h2>
                    {loading && <LoadingSpinner loadingText='Ładowanie parametrów...' />}
                    {!loading && !hasError && (
                        <>
                            <Nav variant='tabs' activeKey={activeTab} onSelect={setActiveTab} className='mb-4'>
                                <Nav.Item>
                                    <Nav.Link key='system' eventKey='system'>Parametry Systemowe</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link key='navBar' eventKey='navBar'>Pasek nawigacji</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link key='slider' eventKey='slider'>Slider</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link key='footer' eventKey='footer'>Stopka i benefity rejestracji</Nav.Link>
                                </Nav.Item>
                                {siteIds.map(id => (
                                    <Nav.Item key={id}>
                                        <Nav.Link eventKey={id}>Oddział {id}</Nav.Link>
                                    </Nav.Item>
                                ))}
                            </Nav>
                            {generateTabContent()}
                        </>
                    )}
                </div>
            </div>
        </ErrorBoundary>
    )
}

export default AdminPanelPageIndex
